// PKT-0653 · what a person is looking for, rendered once.
//
// The first extraction toward one person sheet. LeadDetailModal and
// ClientRecordDialog both draw this chip row — areas and pets — from the same
// helpers, in near-identical JSX, one with a heading and one without. The
// client dialog's copy called formatLeadPets THREE TIMES in a single
// expression, which is what duplicated markup turns into once nobody is
// reading it as a whole.
//
// It takes the FIELDS, not a Lead: the two modals hold different row types
// (Lead and DashboardLeadRow), and a shared section that demands one of them
// only moves the duplication somewhere else. Same reasoning as
// VerificationSubject, one section over.
//
// Renders nothing when there is nothing to say — an empty "Looking for" is a
// heading over a blank, which reads as data that failed to load.

import React from "react";
import { Box, Chip, Stack, Typography } from "@mui/material";
import PlaceIcon from "@mui/icons-material/Place";
import PetsIcon from "@mui/icons-material/Pets";
import { buildLeadLocationParts, formatLeadPets } from "../../lib/leadPreferences";

export interface PersonPreferencesProps {
  neighborhoods?: string[] | string | null;
  neighborhoodOther?: string | null;
  pets?: string | null;
  /** Shown above the chips. Omit where the surrounding section already says it. */
  heading?: string;
  headingSx?: object;
}

export default function PersonPreferences({
  neighborhoods,
  neighborhoodOther,
  pets,
  heading,
  headingSx,
}: PersonPreferencesProps) {
  const areas = buildLeadLocationParts(neighborhoods, neighborhoodOther);
  const petText = formatLeadPets(pets);
  if (areas.length === 0 && !petText) return null;

  return (
    <Box data-testid="person-preferences">
      {heading && <Typography sx={headingSx}>{heading}</Typography>}
      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
        {areas.map((n) => (
          <Chip
            key={n}
            size="small"
            icon={<PlaceIcon sx={{ fontSize: 14 }} />}
            label={n}
            variant="outlined"
            data-testid={`person-area-${n}`}
          />
        ))}
        {petText && (
          <Chip
            size="small"
            icon={<PetsIcon sx={{ fontSize: 14 }} />}
            label={petText}
            color="primary"
            variant="outlined"
            data-testid="person-pets"
          />
        )}
      </Stack>
    </Box>
  );
}
